import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ISeriesBuilder, ISeriesBuilderArguments } from './ISeriesBuilder';
import { SeriesMappingResult } from '.';
import { getGroupName, mapConfiguration, momentFormat } from '../../common/';
import { LogbookTableConfiguration } from '../../widgets/logbookTable/LogbookTable.configuration';
import { AugmentedEntry, Dictionary, DigitalSetSimpleDictionary, GroupDisplayName, IValueViewModel, LogbookSeriesValue, TagStringValueDto, TimePeriod } from '../../models';
import { HeadingDefinition } from '../../widgets/logbookTable/HeadingDefinition';
import { IFormatNumberHelper, IValueFormattingService } from '../../services';
import { formatValueForCsv } from '../../services/data/formatValueForCsv';
import { LogbookSeriesValues } from 'widgets/logbookTable/LogbookSeriesValues';
import { LogbookTableSeries } from 'widgets/logbookTable';

export interface ILogbookTableService extends ISeriesBuilder {
    buildCsvTable(data: AugmentedEntry[], configuration: LogbookTableConfiguration, headings: HeadingDefinition[][]): string[][];
    mapEntryValues(
        entryId: number,
        series: LogbookTableSeries[],
        allValues: LogbookSeriesValues[],
        viewModels: Dictionary<IValueViewModel>,
        configuration: {dateFormat?: string},
        digitalSets: DigitalSetSimpleDictionary,
        stringValues?: TagStringValueDto[]
    ): LogbookSeriesValue[];
}

export class LogbookTableService implements ILogbookTableService {
    static serviceId = 'logbookTableService';

    static $inject = ['$q', 'formatNumberHelper', 'valueFormattingService'];

    constructor(
        private _$q: angular.IQService,
        private _formatNumberHelper: IFormatNumberHelper,
        private _valueFormattingService: IValueFormattingService
    ) {}

    buildSeriesFromViewModels(args: ISeriesBuilderArguments): angular.IPromise<SeriesMappingResult> {
        const { configuration, viewModels, map, errors } = args;
        const mapped = mapConfiguration(configuration.series, viewModels, map, errors);

        const series = mapped.series
            .filter((s) => !s.missing)
            .map((s) => {
                const res = Object.assign({}, s);
                res.configuration = Object.assign({}, res.configuration);
                if (map) {
                    res.configuration.guid = map[s.configuration.guid] ?? s.configuration.guid;
                }
                return res;
            });

        const result = (Object as any).assign(mapped, { map, series });
        return this._$q.when(result);
    }

    buildCsvTable(data: AugmentedEntry[], configuration: LogbookTableConfiguration, headings: HeadingDefinition[][]): string[][] {
        const formatter = this._formatNumberHelper.getFormatter(null, false);
        const table: string[][] = [];
        headings.forEach((heading, i) => {
            let labels = heading.map((d) => d.label);
            if (configuration.hasSeverity) {
                labels.splice(0, 1);
                if (i === 0) {
                    labels = ['Entry ID', 'Severity'].concat(labels);
                } else {
                    labels = ['', ''].concat(labels);
                }
            }
            table.push(labels);
        });
        const format = configuration.dateFormat ? configuration.dateFormat : momentFormat;
        const levels = ['Information', 'Warning', 'Critical'];
        data.forEach((entry) => {
            const values: string[] = [];
            if (configuration.hasSeverity) {
                values.push(entry.id.toString());
                values.push(levels[entry.severity]);
            }
            if (configuration.timeRange === TimePeriod.Both) {
                values.push(`${moment(entry.range.startDate).format(format)} - ${moment(entry.range.endDate).format(format)}`);
            } else if (configuration.timeRange === TimePeriod.BeginDate) {
                values.push(moment(entry.range.startDate).format(format));
            } else if (configuration.timeRange === TimePeriod.EndDate) {
                values.push(moment(entry.range.endDate).format(format));
            }
            if (configuration.hasDuration) {
                values.push(entry.displayDuration);
            }
            if (configuration.groupNameDisplayed !== GroupDisplayName.None) {
                values.push(getGroupName(entry.group, configuration.groupNameDisplayed));
            }
            if (configuration.hasIdentifierName) {
                values.push(entry.identifierViewModel?.name);
            }
            if (configuration.hasIdentifierShortComment) {
                values.push(entry.identifierViewModel?.shortComment);
            }
            if (configuration.hasDescription) {
                values.push(entry.comment);
            }
            if (configuration.hasCategories) {
                values.push(entry.labels.join(','));
            }
            if (configuration.hasUser) {
                values.push(entry.user);
            }
            if (configuration.hasDateModified) {
                values.push(entry.dateModified);
            }
            if (entry.values) {
                entry.values.forEach((v) => {
                    let { formattedValue } = v;
                    if (v.value) {
                        formattedValue = formatValueForCsv(v.value, formatter);
                    }
                    values.push(formattedValue);
                });
            }
            table.push(values);
        });
        return table;
    }

    mapEntryValues(
        entryId: number,
        series: LogbookTableSeries[],
        allValues: LogbookSeriesValues[],
        viewModels: Dictionary<IValueViewModel>,
        configuration: { dateFormat?: string },
        digitalSets: DigitalSetSimpleDictionary,
        stringValues?: TagStringValueDto[]
    ): LogbookSeriesValue[] {
        let mappedValues: LogbookSeriesValue[] = [];
        if (series?.length > 0 && allValues.length > 0) {
            const values = _.find(allValues, (v) => !!v && v.logbookEntryId === entryId);
            if (values) {
                mappedValues = series.map((s) => {
                    let found = _.find(
                        values.values,
                        (v) => !!v && v.originalGuid === s.guid && v.aggregationMode === s.aggregationMode && v.valueType === s.value
                    );

                    if (!found) {
                        found = { formattedValue: '-' } as LogbookSeriesValue;
                    } else {
                        const vm = viewModels[found.guid];
                        found = angular.copy(found);
                        found.formattedValue = this._valueFormattingService.formatValue(
                            found,
                            vm,
                            digitalSets,
                            s.aggregationMode,
                            s.decimals,
                            configuration.dateFormat,
                            stringValues
                        );
                    }
                    return found;
                });
            }
        }
        return mappedValues;
    }
}